<template>
  <BasePermission
    :permission="permission"
    :model-value="isSelected"
    :disabled="disabled"
    :has-extra-content="abacEnabled"
    @update:model-value="handleSelectionChange"
  >
    <template #extra-content>
      <div class="pt-4 flex flex-col gap-3">
        <template v-for="action in actionsConfig" :key="action.key">
          <PermissionToggleAction
            v-if="action.type === 'toggle'"
            :action-name="action.name"
            :model-value="extraValues?.[action.key] || false"
            :disabled="disabled"
            @update:model-value="
              (value) => handleExtraUpdate(action.key, value)
            "
          />

          <PermissionScopeAction
            v-else-if="action.type === 'scope'"
            :action-name="action.name"
            :action-key="action.key"
            :model-value="extraValues?.[action.key] || POLICY_SCOPES.NONE"
            :scope-options="scopeOptions"
            :disabled="disabled"
            :permission-key="permission.key"
            :object-properties="objectProperties"
            :condition-data="getConditionData(action.key)"
            :users-teams-options="usersTeamsOptionsByTab"
            @update:model-value="
              (value) => handleExtraUpdate(action.key, value)
            "
            @condition-update="handleConditionUpdate"
          />
        </template>
      </div>
    </template>
  </BasePermission>
</template>

<script setup>
import BasePermission from "./BasePermission.vue";
import PermissionToggleAction from "./PermissionToggleAction.vue";
import PermissionScopeAction from "./PermissionScopeAction.vue";
import { computed, onMounted } from "vue";
import { useStore } from "vuex";
import {
  POLICY_SCOPES,
  getCRMScopeOptions,
} from "@/src/constants/permissionConstants.js";

const props = defineProps({
  permission: { type: Object, required: true },
  modelValue: { type: Boolean, required: true },
  disabled: { type: Boolean, default: false },
  extraValues: { type: Object, default: () => ({}) },
  abacEnabled: { type: Boolean, default: true },
});

const emit = defineEmits(["update:modelValue", "update:extra"]);

const store = useStore();
const isSelected = computed(() => props.modelValue);

const actionsConfig = [
  { type: "toggle", name: "Create", key: "create" },
  { type: "scope", name: "View", key: "view" },
  { type: "scope", name: "Edit", key: "edit" },
  { type: "scope", name: "Delete", key: "delete" },
  { type: "toggle", name: "Export", key: "export" },
  { type: "toggle", name: "Import", key: "import" },
];

const objectName = computed(() => {
  // Use the display name for labels, not the key (which could be an ID)
  return props.permission.name || props.permission.key;
});

const scopeOptions = computed(() => {
  return getCRMScopeOptions(objectName.value);
});

const objectProperties = computed(() => {
  const objectSlug = props.permission.key.toLowerCase();
  const attributes =
    store.getters["objects/getAttributesBySlugOrId"](objectSlug);
  return attributes || [];
});

const usersTeamsOptionsByTab = computed(() => {
  return store.getters["users/accessOptionsByTab"] || { Users: [], Teams: [] };
});

const getConditionData = (actionKey) => {
  const conditionKey = `${actionKey}_condition`;
  return props.extraValues?.[conditionKey] || null;
};

const handleSelectionChange = (checked) => {
  emit("update:modelValue", checked);
};

const handleExtraUpdate = (key, value) => {
  emit("update:extra", { key: props.permission.key, inputKey: key, value });
};

const handleConditionUpdate = ({ actionKey, key, value }) => {
  const conditionKey = `${actionKey}_condition`;
  const currentCondition = props.extraValues?.[conditionKey] || {};

  const updatedCondition = {
    ...currentCondition,
    [key]: value,
  };

  emit("update:extra", {
    key: props.permission.key,
    inputKey: conditionKey,
    value: updatedCondition,
  });
};

onMounted(async () => {
  if (!store.getters["users/users"]) {
    await store.dispatch("users/fetchUsers");
  }
  if (!store.getters["users/teams"]) {
    await store.dispatch("users/fetchTeams");
  }

  if (!store.getters["objects/objects"].length) {
    await store.dispatch("objects/fetchObjects");
  }
});
</script>

<style scoped>
.crm_view_select :deep(.ant-select-dropdown) {
  width: auto !important;
}
</style>
